<template>
  <div>
    <div class="weather full-width-container" v-for="(weatherZone, index) in reversedWeatherZones">
      <div class="flex-grid row">
        <h1 class="title col flex-grow-1 hide-mobile">{{ weatherZone.Location }} Weather</h1>

        <UnitToggles v-if="index === 0" />
      </div>

      <div class="flex-grid row">
        <div class="col current-weather-block">
          <Current :weatherZone="weatherZone" />
        </div>

        <div class="col snow-depth-block" :class="{ 'no-snow-base': !showSnowBase }">
          <div class="storm-total-text" :class="{'show-metric': measureInMetric}" v-if="stormTotal && index === 0">
            <div class="storm-total-icon"></div>
            <p>
              <a href="#storm-radar" @click.prevent="toggleStormRadar">
                <span class="ttu">
                  {{ textOptions.storm_snowfall }}
                </span> {{ stormTotal }}
              </a>
            </p>
            <StormRadar :embed="snow_report.StormRadar" v-if="showStormRadar" v-on:closeStormModal="closeStormModal"></StormRadar>
          </div>

          <template v-if="showSnowBase">
            <p class="snow-base-range-text" id="snowbase-cm-metric">
              <span id="snowbase-cm-imperial">{{ snowBase(weatherZone.Location) }}</span>

              <span class="depth-measurement" :class="{'show-metric': measureInMetric}">
                <a href="#snowbase-cm-metric" class="cm" @click.prevent="toggleSnowUnit('metric')">
                  <span tabindex="-1">cm</span>
                </a>
                <a href="#snowbase-cm-imperial" class="in" @click.prevent="toggleSnowUnit('imperial')">
                  <span tabindex="-1">in</span>
                </a>
              </span>
            </p>

            <div class="snow-base-text ttu o-55">Snow Base</div>
          </template>
        </div>

        <div class="col conditions-block">
          <Conditions :currentArea="areaForLocation(weatherZone.Location)" :snowbaseUnit="snowbaseUnit" :snowReport="snow_report" />
        </div>

        <div class="col forecast-snowfall-block">
          <Forecast :forecastDays="forecastDays" />
          <Snowfall :forecastDays="forecastDays" />
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import UnitToggles from './Weather/UnitToggles.vue';
import Conditions from './Weather/Conditions.vue';
import Current from './Weather/Current.vue';
import Forecast from './Weather/Forecast.vue';
import Snowfall from './Weather/Snowfall.vue';
import StormRadar from './Weather/StormRadar.vue';

export default {
  name: 'WeatherStacked',
  data() {
    return {
      showStormRadar: false,
    };
  },
  components: {
    Conditions,
    Current,
    Forecast,
    Snowfall,
    StormRadar,
    UnitToggles,
  },
  computed: {
    base() {
      return this.snow_report.BaseArea;
    },
    mid() {
      return this.snow_report.MidMountainArea;
    },
    summit() {
      return this.snow_report.SummitArea;
    },
    showSnowBase() {
      if (!this.weatherPrimaryLayoutOption) { return; }

      return this.weatherPrimaryLayoutOption === 'toggle_base';
    },
    stormTotal() {
      if (this.snow_report.StormTotalIn === '--') { return; }
      if (this.snow_report.StormTotalIn === '0.0') { return; }
      if (typeof (this.snow_report.StormTotalIn) === 'undefined') { return; }

      return this.snowbaseUnit === 'metric'
        ? `${this.snow_report.StormTotalCM.split('.0')[0]} cm`
        : `${this.snow_report.StormTotalIn.split('.0')[0]} in`;
    },
    forecastDays() {
      if (this.forecast) {
        return [
          this.forecast.TwoDay,
          this.forecast.ThreeDay,
          this.forecast.FourDay,
        ];
      }
      return [];
    },
    measureInMetric() {
      return this.snowbaseUnit == 'metric';
    },
    ...mapState([
      'weatherZones',
      'snow_report',
      'forecast',
      'snowbaseUnit',
    ]),
    ...mapGetters([
      'weatherPrimaryLayoutOption',
      'reversedWeatherZones',
      'textOptions',
    ]),
  },

  methods: {
    areaForLocation(location) {
      return this[location];
    },
    snowBase(location) {
      if (typeof (this.snow_report.StormTotalIn) === 'undefined') { return; }

      return this.snowbaseUnit === 'metric'
        ? this.areaForLocation(location).BaseCm.split('.')[0]
        : this.areaForLocation(location).BaseIn.split('.')[0];
    },
    toggleStormRadar() {
      if (this.snow_report.StormRadar === '--') { return false; }

      this.showStormRadar = true;
    },
    closeStormModal() {
      this.showStormRadar = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.weather {
  + .weather {
    border-top: 1px solid $brand-gray;
    padding-top: 40px;
  }
  .flex-grid {
    @media screen and (max-width: 1400px) {
      flex-flow: wrap;
      > .col {
        flex-grow: 0;
        &.flex-grow-1 {
          flex-grow: 1;
        }
      }
    }
  }
}

.current-weather-block {
  order: 1;
  max-width: 250px;
  flex-basis: 55%;
  @media (min-width: $mobile) {
    flex-basis: 38%;
  }
  p {
    font-family: $base-font-regular;
    font-size: 14px;
  }
}

.snow-depth-block {
  order: 2;
  flex-basis: 35%;
  max-width:  120px;
  position: relative;
  top: 40px;
  @media (min-width: $mobile) {
    flex-basis: 24%;
  }
  @media (min-width: $tablet) {
    top: 50px;
    flex-basis: 25%;
  }
  @media screen and (min-width: $tablet) {
    flex-basis: 25%;
  }
  &.no-snow-base {
    flex-basis: 0;
  }
  .snow-base-range-text {
    color: $dark-gray;
    font-family: $base-font-regular;
    font-size: 38px;
    letter-spacing: -2px;
    line-height: 1.2;
    margin: -10px 0 0 0;
    min-height: 70px;
    @media (min-width: $tablet) {
      font-size: 62px;
    }
  }
  .snow-base-text {
    color: $dark-gray;
    font-size: 13px;
    line-height: 1.2;
    margin-left: 0%;
    margin-top: 0;
  }
  .storm-total-text {
    background-color: $alert-bar-background;
    color: $white;
    border-radius: 30px;
    white-space: nowrap;
    padding: 5px 0;
    position: absolute;
    text-align: left;
    left: 0px;
    top: -55px;
    min-width: 150px;
    width: 100%;
    &.show-metric {
      min-width: 165px;
    }
    @media (max-width: 350px) {
      left: -20px;
    }
    @media (min-width: $mobile) {
      left: 0;
      min-width: 200px;
      &.show-metric {
        min-width: 210px;
      }
    }
    @media (min-width: $tablet) {
      top: -50px;
      min-width: 210px;
    }
    @media (min-width: 1300px) {
      top: -90px;
    }
    p {
      font-family: $base-font-bold;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.2;
      margin: 4px 0 -4px;
      span {
        font-family: $base-font-regular;
        font-size: 12px;
        font-weight: 500;
      }
      @media (min-width: $mobile) {
        font-size: 14px;
        span {
          font-size: 15px
        }
      }
    }
    .storm-total-icon {
      background: url('~@/assets/icons/snowflake.svg') no-repeat center center;
      background-size: cover;
      float: left;
      height: 25px;
      margin: -2px 2px 0 0;
      width: 25px;
      @media (min-width: $mobile) {
        height: 35px;
        margin: -5px 2px -5px 0;
        width: 35px;
      }
    }
  }
  #snowbase-cm-imperial {
    display: inline-block;
    float: left;
    width: 100%;
  }
  .depth-measurement {
    color: $dark-gray;
    display: inline-block;
    float: right;
    font-family: $base-font-regular;
    font-size: 12px;
    letter-spacing: 0;
    position: relative;
    right: -10px;
    top: -58px;
    @media (max-width: $tablet) {
      top: -35px;
    }
    a {
      display: block;
      &.cm {
        display: none;
      }
    }
    &.show-metric  {
      .in {
        display: none;
      }
      .cm {
        display: block;
      }
    }
  }
}

.conditions-block {
  left: 52%;
  flex-basis: 50%;
  max-width: 240px;
  order: 3;
  position: relative;
  top: -25px;
  width: 100%;
  @media (min-width: $mobile) {
    left: 0;
    flex-basis: 25%;
    top: 30px;
  }
  @media (min-width: $tablet) {
    flex-basis: 30%;
    order: 3;
    top: 50px;
  }
  @media screen and (min-width: 1300px) {
    order: 4;
    flex-grow: 0;
    flex-basis: 33%;
    left: 105px;
    top: -50px;
  }
  @media screen and (min-width: 1500px) {
    flex-basis: 36%;
    left: 0;
    order: 3;
    top: 50px;
  }
}

.weather {
  .flex-grid > {
    .forecast-snowfall-block {
      background-color: $body-bg;
      margin-bottom: -20px;
      flex-basis: 120%;
      margin: 0px -20px -20px;
      padding: 20px 0;
      order: 4;
      flex-basis: 120%;
      @media (min-width: $mobile) {
        background-color: transparent;
        margin-bottom: 0;
        margin-top: 20px;
        padding: 0;
        flex-basis: 100%;
      }
      @media screen and (min-width: 1300px) {
        flex-basis: 45%;
        order: 3;
      }
      @media screen and (min-width: 1400px) {
        flex-basis: 55%;
      }
      @media screen and (min-width: 1500px) {
        margin-top: 20px;
        flex-basis: 32%;
        order: 4;
      }
    }
  }
}
</style>
